import { ACCOUNT_FILTER_FIELDS, ACCOUNT_SORT_FIELDS, ACCOUNT_SEARCH_FIELDS, FilterKey } from '@repo/types';
import { ACCOUNT_STATE, GENDER } from '@theailab-dev/parchment';
import type { AccountState, Gender } from '@theailab-dev/parchment';
import React, { useState, useEffect } from 'react';
import { HiSearch, HiFilter, HiRefresh } from 'react-icons/hi';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TablePageLayout } from '../../components/Layout/TablePageLayout';
import {
  Button,
  Input,
  Card,
  Badge,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  LoadingSpinner,
} from '../../components/ui';
import { useAccounts } from '../../hooks/useAccounts';

/**
 * Users 컴포넌트 - 사용자 목록 페이지
 */
const Users: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const currentLimit = parseInt(searchParams.get('limit') || '20', 10);
  const filterType = (searchParams.get('filterType') as 'state' | 'gender' | '') || '';
  const filterValue = searchParams.get('filterValue') || '';
  const searchKeyword = searchParams.get('search') || '';

  const [searchQuery, setSearchQuery] = useState(searchKeyword);

  const getFilterOptions = () => {
    if (!filterType) return [];

    const filterField = ACCOUNT_FILTER_FIELDS.find((field) => field.key === filterType);

    if (!filterField) return [];

    return filterField.values.map((fv) => ({
      value: fv.value,
      label: filterType === 'gender' ? getGenderLabel(fv.value as Gender) : fv.value,
    }));
  };

  const { data, isLoading, isError, error, refetch } = useAccounts({
    page: currentPage,
    limit: currentLimit,
    filterKey: searchKeyword ? FilterKey.SEARCH : filterType && filterValue ? (filterType as FilterKey) : undefined,
    filterValue: searchKeyword || (filterType && filterValue ? filterValue : undefined),
  });

  useEffect(() => {
    setSearchQuery(searchKeyword);
  }, [searchKeyword]);

  useEffect(() => {
    if (!searchParams.has('page')) {
      setSearchParams({ page: '1' }, { replace: true });
    }
  }, []);

  const updateUrlParams = (params: {
    page?: number;
    limit?: number;
    filterType?: string;
    filterValue?: string;
    search?: string;
  }) => {
    const newParams = new URLSearchParams(searchParams);

    if (params.page !== undefined) {
      newParams.set('page', params.page.toString());
    }

    if (params.limit !== undefined) {
      newParams.set('limit', params.limit.toString());
    }

    if (params.filterType !== undefined) {
      if (params.filterType) {
        newParams.set('filterType', params.filterType);
      } else {
        newParams.delete('filterType');
        newParams.delete('filterValue');
      }
    }

    if (params.filterValue !== undefined) {
      if (params.filterValue) {
        newParams.set('filterValue', params.filterValue);
      } else {
        newParams.delete('filterValue');
      }
    }

    if (params.search !== undefined) {
      if (params.search) {
        newParams.set('search', params.search);
      } else {
        newParams.delete('search');
      }
    }

    setSearchParams(newParams);
  };

  const handleSearch = () => {
    const keyword = searchQuery.trim();
    updateUrlParams({ page: 1, search: keyword });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handlePageChange = (page: number) => {
    updateUrlParams({ page });
  };

  const handleLimitChange = (newLimit: number) => {
    updateUrlParams({ limit: newLimit, page: 1 });
  };

  const getStateBadgeVariant = (state: AccountState) => {
    switch (state) {
      case ACCOUNT_STATE.NORMAL:
        return 'success';
      case ACCOUNT_STATE.DELETED:
        return 'danger';
      default:
        return 'neutral';
    }
  };

  const getGenderBadgeVariant = (gender: Gender | null | undefined) => {
    if (!gender) return 'neutral';
    switch (gender) {
      case GENDER.MALE:
        return 'blue';
      case GENDER.FEMALE:
        return 'purple';
      default:
        return 'neutral';
    }
  };

  const getGenderLabel = (gender: Gender | null | undefined) => {
    if (!gender) return '-';
    switch (gender) {
      case GENDER.MALE:
        return '남성';
      case GENDER.FEMALE:
        return '여성';
      default:
        return gender;
    }
  };

  if (isError) {
    return (
      <div className="flex h-full w-full items-center justify-center p-8">
        <Card className="w-full max-w-md p-8 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
            <svg className="h-8 w-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h3 className="mb-2 text-lg font-bold text-slate-900">오류가 발생했습니다</h3>
          <p className="mb-6 text-slate-500">
            {error instanceof Error ? error.message : '데이터를 불러오는 중 오류가 발생했습니다.'}
          </p>
          <Button onClick={() => refetch()} variant="outline">
            다시 시도
          </Button>
        </Card>
      </div>
    );
  }

  const accounts = data?.data || [];
  const meta = data?.meta;
  const total = meta?.total || 0;
  const page = meta?.page || 1;
  const totalPages = meta?.totalPages || 1;
  const limit = meta?.limit || 20;

  return (
    <TablePageLayout
      title="사용자 목록"
      description={
        total > 0
          ? `총 ${total.toLocaleString()}명의 사용자가 등록되어 있습니다`
          : '시스템에 등록된 사용자 목록을 확인하세요'
      }
      filters={
        <>
          <div className="relative">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <HiFilter className="text-slate-400" />
            </div>
            <select
              value={filterType}
              onChange={(e) => {
                const newType = e.target.value as 'state' | 'gender' | '';
                updateUrlParams({
                  page: 1,
                  filterType: newType,
                  filterValue: '',
                });
              }}
              className="cursor-pointer appearance-none rounded-lg border border-slate-200 bg-white py-2 pr-8 pl-9 text-sm text-slate-700 transition-colors hover:border-slate-300 focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none">
              <option value="">필터 선택</option>
              {ACCOUNT_FILTER_FIELDS.filter((field) => field.key !== 'kind').map((field) => (
                <option key={field.key} value={field.key}>
                  {field.label}
                </option>
              ))}
            </select>
          </div>

          <select
            value={filterValue}
            onChange={(e) => {
              const newValue = e.target.value;
              updateUrlParams({ page: 1, filterType, filterValue: newValue });
            }}
            disabled={!filterType}
            className="min-w-[140px] cursor-pointer rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700 transition-colors hover:border-slate-300 focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:bg-slate-50 disabled:text-slate-400">
            <option value="">값 선택</option>
            {getFilterOptions().map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          {(searchKeyword || (filterType && filterValue)) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSearchQuery('');
                updateUrlParams({
                  page: 1,
                  search: '',
                  filterType: '',
                  filterValue: '',
                });
              }}
              leftIcon={<HiRefresh />}
              className="text-slate-500">
              초기화
            </Button>
          )}
        </>
      }
      search={
        <>
          <div className="relative w-full lg:w-80">
            <Input
              placeholder={`${ACCOUNT_SEARCH_FIELDS.map(
                (field) => ACCOUNT_SORT_FIELDS.find((sf) => sf.field === field)?.label || field,
              ).join(', ')}로 검색...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              leftIcon={<HiSearch />}
              className="w-full"
            />
          </div>
          <Button onClick={handleSearch} variant="secondary">
            검색
          </Button>
        </>
      }
      isLoading={isLoading}
      currentPage={page}
      totalPages={totalPages}
      onPageChange={handlePageChange}
      totalItems={total}
      limit={limit}
      onLimitChange={handleLimitChange}>
      {isLoading ? (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/50">
          <LoadingSpinner />
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              {(() => {
                const filteredFields = ACCOUNT_SORT_FIELDS.filter(
                  (field) =>
                    (ACCOUNT_SEARCH_FIELDS as readonly string[]).includes(field.field) || field.field === 'state',
                );

                const hasGender = filteredFields.some((f) => f.field === 'gender');
                const fieldsToShow = hasGender
                  ? filteredFields
                  : [
                      ...filteredFields,
                      ACCOUNT_SORT_FIELDS.find((f) => f.field === 'gender') || { field: 'gender', label: '성별' },
                    ];

                return fieldsToShow.map((field) => <TableHead key={field.field}>{field.label}</TableHead>);
              })()}
            </TableRow>
          </TableHeader>
          <TableBody>
            {accounts.map((account) => (
              <TableRow key={account.id} onClick={() => navigate(`/dashboard/users/${account.id}`)}>
                <TableCell nowrap className="text-slate-500">
                  #{account.id}
                </TableCell>
                <TableCell nowrap className="font-medium text-slate-900">
                  {account.email}
                </TableCell>
                <TableCell nowrap>{account.name}</TableCell>
                <TableCell nowrap>{account.phone}</TableCell>
                <TableCell nowrap>
                  <Badge variant={getStateBadgeVariant(account.state)}>{account.state}</Badge>
                </TableCell>
                <TableCell nowrap>
                  <Badge variant={getGenderBadgeVariant(account.gender)}>{getGenderLabel(account.gender)}</Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {!isLoading && accounts.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-slate-50">
            <svg className="h-8 w-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-slate-900">등록된 사용자가 없습니다</h3>
          <p className="mt-1 text-slate-500">검색 조건을 변경하거나 새로운 사용자를 기다려주세요.</p>
        </div>
      )}
    </TablePageLayout>
  );
};

export default Users;
